import React, {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useRouter} from 'next/router';
import useCommon from '@utils/hooks/common';
import PopupContainer from '@components/layout/PopupContainer';
import GradientButton from '@components/GradientButton';
import styles from './styles';

interface TournamentCancelModalSheetProps {
  isModalSheetOpen: boolean;
  onModalSheetClose: () => void;
  tournamentName: string;
  refundAmount?: number;
}

const TournamentCancelModalSheet: React.FC<TournamentCancelModalSheetProps> = ({
  isModalSheetOpen,
  onModalSheetClose,
  tournamentName,
  refundAmount,
}) => {
  const {t, system, classes} = useCommon({
    withTranslation: ['tournament', 'common'],
    withStyles: {styles, hasProps: true},
    observers: ['system'],
  });
  const router = useRouter();

  useEffect(() => {
    const handleModalClose = () => {
      onModalSheetClose();
    };
    system.on('tournamentCancelOnClose', handleModalClose);
    return () => {
      system.off('tournamentCancelOnClose', handleModalClose);
    };
  }, [onModalSheetClose, system]);

  const handleBackToLobby = () => {
    onModalSheetClose();
    // Navigate to home page and force refresh
    router.push('/home').then(() => {
      // Force a hard refresh of the page
      window.location.reload();
    });
  };

  return (
    <PopupContainer
      visible={isModalSheetOpen}
      onCancel={() => {}} // Disable backdrop click
      title={t('tournament:cancel.title')}
      mobileModalProps={{
        snapPoints: [400, 0],
        style: {overflow: 'hidden', overflowY: 'hidden'}
      }}
      closable={false} // Remove close button
    >
      <div className={classes.container}>
        <div className={classes.contentArea}>
          <div className={classes.imageContainer}>
            <img
              src="/images/tournament/tournament_cancel.svg"
              alt="Tournament Cancelled"
              className={classes.cancelImage}
            />
          </div>

          <div className={classes.textContainer}>
            <p className={classes.description}>
              {t('tournament:cancel.description', {tournamentName})}
            </p>

            {refundAmount && refundAmount > 0 && (
              <p className={classes.refundMessage}>
                {t('tournament:cancel.refund_message', {amount: refundAmount})}
              </p>
            )}
          </div>
        </div>

        <div className={classes.buttonContainer}>
          <GradientButton
            isBlue={true}
            style={{height: 50}}
            className={classes.closeButton}
            onClick={handleBackToLobby}
          >
            {t('common:back_to_lobby')}
          </GradientButton>
        </div>
      </div>
    </PopupContainer>
  );
};

export default TournamentCancelModalSheet;
